<script lang="ts" setup>
import type { ITableHeader } from "@/components/ui/Table/table.interface"
import type { Guest, IRsvpAdditionalGuest, RsvpStatus } from "@/models"
import { useQueryClient } from "@tanstack/vue-query"
import { computed, ref, useTemplateRef } from "vue"
import { useDeleteGuest, useExportRsvpList, useGetEventVault, useGetInvitationConfig, useListGuestsV1 } from "@/api"
import { ConfirmModal, DataCard, GuestDetailsModal, RsvpGuestListFilterDrawer, RsvpListActionsMenu, RsvpStatusPill } from "@/components"
import { Pagination, Table } from "@/components/ui"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useConvertedRouteParam, useModal, usePagination, useTableColumns, useTranslation } from "@/composables"
import { SidebarHostEventLayout } from "@/layouts"
import { RsvpListFiltersDrawerKey } from "@/symbols"
import { ViewMode } from "@/utils"
import RsvpListControls from "./RsvpListControls.vue"
import { useGuestSelection } from "./useGuestSelection"
import { useRsvpListFilters } from "./useRsvpListFilters"

definePage({
  meta: {
    requiresAuth: true,
  },
})

export interface GuestTableRow {
  id?: number
  name?: string
  email?: string
  rsvpStatus?: RsvpStatus
  rsvpComment?: string
  dietaryRequirements?: string
  songRequests?: string
  rsvpCount?: number
  rsvpUpdatedDate?: string
  picsCount?: number
  vidsCount?: number
  messagesCount?: number
  additionalGuests?: IRsvpAdditionalGuest[]
  actions?: unknown
}

type AdditionalGuestTableRow = Pick<GuestTableRow, "name" | "email" | "rsvpStatus" | "dietaryRequirements" | "songRequests">

const eventVaultId = useConvertedRouteParam("id", "/event/[id]/rsvp-list/", value => Number(value))

const { t, d } = useTranslation()
const toast = useToast()
const queryClient = useQueryClient()

const { selectedGuest, isConfirmModalOpen, selectGuestForUpdate, selectGuestForDelete, clearGuest, toggleGuestDetailsModal, toggleConfirmModal } = useGuestSelection()

const guestDetailsModalRef = useTemplateRef<InstanceType<typeof GuestDetailsModal> | null>("guestDetailsModalRef")
const rsvpListWrapperRef = useTemplateRef<HTMLElement | null>("rsvpListWrapperRef")
const mode = ref<ViewMode>(ViewMode.TABLE)

const { toggle: toggleFilters } = useModal(RsvpListFiltersDrawerKey)

const { filters, searchInput, rsvpStatusFilterCount, otherFiltersCount, toggleRsvpStatusFilter, clearRsvpStatusFilters, showAllRsvpStatusFilters, updateFilters, syncWithPagination } = useRsvpListFilters({ initialPerPage: 50, debounceDelay: 500 })

const { data: guestsData, isLoading: isGuestsLoading, pageCount } = useListGuestsV1(eventVaultId, filters)

const { data: eventVault, isLoading: isEventVaultLoading } = useGetEventVault(eventVaultId)
const { data: invitationConfig } = useGetInvitationConfig(eventVaultId)

const isLoading = computed(() => isGuestsLoading.value || isEventVaultLoading.value)

const totalAcceptedCount = computed(() => guestsData.value?.meta?.extensions?.acceptedGuestCount ?? 0)
const totalMaybeCount = computed(() => guestsData.value?.meta?.extensions?.maybeGuestCount ?? 0)
const totalDeclinedCount = computed(() => guestsData.value?.meta?.extensions?.declinedGuestCount ?? 0)

const isFreemiumEvent = computed(() => eventVault.value?.freemiumMode ?? false)

const guests = computed<GuestTableRow[]>(() => {
  return (
    guestsData.value?.guests.map((guest: Guest) => ({
      id: guest.id,
      name: guest.name,
      email: guest.email,
      rsvpStatus: guest.rsvpStatus,
      rsvpComment: guest.rsvpComment,
      dietaryRequirements: guest.dietaryRequirements,
      songRequests: guest.songRequests,
      rsvpCount: guest.rsvpCount,
      rsvpUpdatedDate: guest?.rsvpUpdatedDate ? d(guest.rsvpUpdatedDate, "short") : undefined,
      picsCount: guest.picsCount ?? 0,
      vidsCount: guest.vidsCount ?? 0,
      messagesCount: guest.messagesCount ?? 0,
      additionalGuests: guest.additionalGuests,
    })) ?? []
  )
})

function canExpandGuestRow(row: GuestTableRow) {
  return Boolean(row.additionalGuests?.length)
}

const allHeaders = computed<ITableHeader<GuestTableRow>[]>(() => [
  { label: t("rsvp-list-page.headers.name"), value: "name" },
  { label: t("rsvp-list-page.headers.email"), value: "email", truncate: true, maxWidth: "max-w-[200px]" },
  { label: t("rsvp-list-page.headers.rsvp_response"), value: "rsvpStatus" },
  { label: t("rsvp-list-page.headers.dietary_notes"), value: "dietaryRequirements", truncate: true },
  { label: t("rsvp-list-page.headers.song_requests"), value: "songRequests", truncate: true },
  { label: t("rsvp-list-page.headers.guest_count"), value: "rsvpCount" },
  { label: t("rsvp-list-page.headers.comment"), value: "rsvpComment", truncate: true },
  { label: t("rsvp-list-page.headers.last_updated"), value: "rsvpUpdatedDate" },
  { label: t("rsvp-list-page.headers.actions"), value: "actions" },
])

const { visibleColumns, headers, toggleColumn, showAllColumns } = useTableColumns(allHeaders, "rsvp-list-visible-columns", { name: true, email: true, rsvpStatus: true, dietaryRequirements: true, songRequests: true, rsvpCount: true, rsvpComment: true, rsvpUpdatedDate: true, actions: true })

const allNestedHeaders = computed<ITableHeader<AdditionalGuestTableRow>[]>(() => [
  { label: t("rsvp-list-page.headers.name"), value: "name" },
  { label: t("rsvp-list-page.headers.email"), value: "email" },
  { label: t("rsvp-list-page.headers.rsvp_response"), value: "rsvpStatus" },
  { label: t("rsvp-list-page.headers.dietary_notes"), value: "dietaryRequirements" },
  { label: t("rsvp-list-page.headers.song_requests"), value: "songRequests" },
])

const nestedHeaders = computed<ITableHeader<AdditionalGuestTableRow>[]>(() =>
  allNestedHeaders.value.filter(header => !!header && !!header.value && visibleColumns.value[String(header.value)]),
)

const pagination = usePagination({ initialPerPage: 50, pageCount, scrollStartRef: rsvpListWrapperRef })

const { onNext, onPrevious } = syncWithPagination(pagination)

const { mutate: deleteGuest, isPending: isDeleteGuestPending } = useDeleteGuest(eventVaultId)
const { mutate: exportRsvpList, isPending: isExportPending } = useExportRsvpList(eventVaultId)

function onDeleteGuestConfirm() {
  deleteGuest(selectedGuest.value?.id, {
    onSuccess: () => {
      toast.success({ description: t("rsvp-list-page.success_delete_message") })
      clearGuest()
      queryClient.invalidateQueries({ queryKey: ["listGuestsV1", eventVaultId] })
      if (isConfirmModalOpen.value)
        toggleConfirmModal()
    },
    onError: () => toast.error(),
  })
}

function onExport() {
  exportRsvpList(undefined, {
    onSuccess: (data) => {
      const url = window.URL.createObjectURL(data.blob)
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", data.filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    },
    onError: () => toast.error(),
  })
}
</script>

<template>
  <SidebarHostEventLayout>
    <div ref="rsvpListWrapperRef" class="mb-8">
      <div class="flex items-start justify-between flex-wrap gap-4">
        <div class="flex flex-col gap-2">
          <h1 class="font-comfortaa font-bold text-xl mb-2">
            {{ t("rsvp-list-page.title") }}
          </h1>
          <p class="text-label text-sm md:text-base font-montserrat">
            {{ t("rsvp-list-page.description") }}
          </p>
        </div>
        <div class="font-montserrat flex flex-col gap-2 text-sm border rounded-md p-4 px-6 w-full lg:w-auto">
          <span class="font-semibold text-base">{{ t("rsvp-list-page.rsvp_summary") }}</span>
          <div class="flex justify-between">
            <span class="text-gray-500">{{ t("rsvp_response_status.attending") }}</span>
            <span class="font-semibold">{{ totalAcceptedCount }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-500">{{ t("rsvp_response_status.maybe") }}</span>
            <span class="font-semibold">{{ totalMaybeCount }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-500">{{ t("rsvp_response_status.declined") }}</span>
            <span class="font-semibold">{{ totalDeclinedCount }}</span>
          </div>
        </div>
      </div>
    </div>

    <RsvpListControls
      v-model:search="searchInput"
      class="mb-4"
      :is-exporting="isExportPending"
      :all-headers="allHeaders"
      :visible-columns="visibleColumns"
      :mode="mode"
      :filters="filters"
      :rsvp-status-filter-count="rsvpStatusFilterCount"
      :other-filters-count="otherFiltersCount"
      :is-freemium-event="isFreemiumEvent"
      @update:mode="(value) => mode = value"
      @show-all-columns="showAllColumns"
      @toggle-column="(value) => toggleColumn(value)"
      @toggle-filters="toggleFilters"
      @clear-rsvp-status-filters="clearRsvpStatusFilters"
      @show-all-rsvp-status-filters="showAllRsvpStatusFilters"
      @toggle-rsvp-status-filter="(value) => toggleRsvpStatusFilter(value)"
      @export="onExport"
      @add-guest="toggleGuestDetailsModal"
    />

    <Transition>
      <Table
        v-if="mode === ViewMode.TABLE"
        :headers="headers"
        :data="guests"
        :is-loading="isLoading"
        :is-row-expandable="(row) => canExpandGuestRow(row)"
        :pagination="pagination"
        :no-data-headline="t('rsvp-list-page.no_data_headline')"
        :no-data-content="t('rsvp-list-page.no_data_content')"
        show-pagination
        @pagination-next="onNext"
        @pagination-previous="onPrevious"
      >
        <template #data-rsvpStatus="{ data: row }">
          <RsvpStatusPill v-if="row.rsvpStatus" :status="row.rsvpStatus" />
        </template>
        <template #expanded-row="{ data: row }">
          <Table :data="row.additionalGuests" :headers="nestedHeaders">
            <template #header>
              <div class="border-b border-secondary px-3 py-2 text-sm font-semibold text-gray-700">
                {{ t("rsvp-list-page.additional_guests", { count: row.additionalGuests?.length ?? 0 }) }}
              </div>
            </template>
            <template #data-rsvpStatus="{ data: additionalGuest }">
              <RsvpStatusPill v-if="additionalGuest.rsvpStatus" :status="additionalGuest.rsvpStatus" />
            </template>
          </Table>
        </template>
        <template #data-actions="{ data: row }">
          <RsvpListActionsMenu :event-vault-id="eventVaultId" :guest="row as Guest" @update="selectGuestForUpdate" @delete="selectGuestForDelete" />
        </template>
      </Table>

      <div v-else>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          <DataCard
            v-for="(guest, index) in guests"
            :key="`guest-${index}`"
            :data="guest"
            :headers="headers"
          >
            <template #data-actions="{ data: row }">
              <RsvpListActionsMenu :event-vault-id="eventVaultId" :guest="row as Guest" @update="selectGuestForUpdate" @delete="selectGuestForDelete" />
            </template>
          </DataCard>
        </div>
        <Pagination
          :current-page="pagination.page.value"
          :page-count="pagination.pageCount?.value"
          :per-page="pagination.perPage.value"
          :can-next="pagination.canNext.value"
          :can-previous="pagination.canPrevious.value"
          @next="onNext"
          @previous="onPrevious"
        />
      </div>
    </Transition>

    <RsvpGuestListFilterDrawer
      :filters="filters"
      @update:filters="updateFilters"
    />

    <GuestDetailsModal
      ref="guestDetailsModalRef"
      :event-vault-id="eventVaultId"
      :guest="selectedGuest"
      :invitation="invitationConfig"
      @cancel="clearGuest"
      @error="() => toast.error()"
      @success="() => {
        toast.success({ description: t('rsvp-list-page.submit_success_message') })
        clearGuest()
      }"
    />

    <ConfirmModal
      v-if="selectedGuest"
      variant="danger"
      :title="t('rsvp-list-page.delete_confirm_title')"
      :message="t('rsvp-list-page.delete_confirm_message', { name: selectedGuest?.name })"
      :is-loading="isDeleteGuestPending"
      @confirm="onDeleteGuestConfirm"
      @cancel="() => {
        clearGuest()
        guestDetailsModalRef?.clearFormValues()
      }"
    />
  </SidebarHostEventLayout>
</template>
